import browserClient from '@apmplus/web';
import type { BrowserInitConfig, BrowserUserConfig } from '@apmplus/web';

import pkg from '../../package.json';

import { PLATFORM } from '@/types/enums/common';
import { getUrlParams } from '@/utils/url';

/** WebPro 应用 ID */
const APM_AID = 1002367;

/** WebPro 应用 Token */
const APM_TOKEN = '1cfecff78df74dfc8553a41ae6e53816';

let initialized = false;

/** 自定义日志级别，与 sendLog 的 level 字段一致 */
export enum ApmLogLevel {
  debug = 'debug',
  info = 'info',
  warn = 'warn',
  error = 'error',
}

export type ApmCustomLogExtra = Record<string, number | string>;

/** 业务侧传入的附加字段 */
export type ApmLogExtraInput = Record<string, string | number | boolean | null | undefined>;

function normalizeExtra(extra?: ApmLogExtraInput): ApmCustomLogExtra | undefined {
  if (!extra) return undefined;
  const result: ApmCustomLogExtra = {};
  for (const [key, value] of Object.entries(extra)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'boolean') {
      result[key] = String(value);
    } else if (typeof value === 'object') {
      result[key] = JSON.stringify(value);
    } else {
      result[key] = value;
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

export interface ApmCustomLogParams {
  /** 日志内容 */
  content: string;
  /** 日志级别 */
  level: ApmLogLevel;
  /** 附加字段 */
  extra?: ApmCustomLogExtra;
  /** 附加日志 */
  attached_log?: string;
}

export type ApmplusInitOptions = Partial<BrowserInitConfig>;

/** URL 中与 release 解析相关的 query 字段 */
export interface ApmReleaseUrlParams {
  /** 平台标识，App 环境为 `APP` */
  platform?: string;
  /** App 原生版本号，由 WebView URL 携带 */
  appVersion?: string;
}

/**
 * 解析 WebPro release 版本号。
 * App 环境且 appVersion 有效 → 返回 App 版本；否则返回 H5 pkg.version。
 */
export function resolveApmRelease(): string {
  const params = getUrlParams<ApmReleaseUrlParams>();
  const platform = params.platform;
  const raw = params.appVersion;
  const appVersion = typeof raw === 'string' ? raw.trim() : '';

  if (platform === PLATFORM.APP && appVersion) {
    return appVersion;
  }
  return pkg.version;
}

/**
 * 静态资源性能插件：按 initiatorType 忽略（会覆盖 SDK 默认列表，需显式带上 xhr/fetch/beacon）
 * @see https://www.volcengine.com/docs/6431/104889?lang=zh#45cf4e0e
 */
const RESOURCE_IGNORE_TYPES = ['xmlhttprequest', 'fetch', 'beacon', 'css', 'script', 'img', 'image', 'link'] as const;

/**
 * JS 错误插件：按 error.message 忽略（与飞书 listeners 中过滤策略对齐）
 * @see https://www.volcengine.com/docs/6431/104889
 */
const JS_ERROR_IGNORE_PATTERNS: (string | RegExp)[] = [
  // 网络 / 用户取消
  'Failed to fetch',
  'Network Error',
  'NetworkError',
  'The operation was aborted',
  'Load failed',
  'bad network response',
  // 部署与资源加载
  'Loading chunk',
  'ChunkLoadError',
  'Importing a module script failed',
  'NotSupportedError',
  // 浏览器自动播放 / 后台页策略
  'Permission was denied',
  'The play() request was interrupted',
  'play() failed',
  "user didn't interact",
  'The requesting page is not visible',
  'not allowed by the user agent',
  // 媒体管线竞态（xgplayer / MSE）
  '_transferCost',
  'this.flv',
  'SourceBuffer',
  'MediaSource attachment is closing',
  'InvalidStateError',
  // 直播 SDK 内部（index.2.1.48.js 等）
  '没有对应的receiver',
  'changeMainOrBackup',
  "Failed to execute 'removeChild'",
  'Web SDK Data init failed',
  // WebRTC 低版本浏览器
  'RTCRtpSender',
  'getCapabilities is not a function',
  // libpag / 其它三方
  'memory access out of bounds',
  'ResizeObserver loop',
  'Script error',
  'DataCloneError',
  "Can't find variable: WeakRef",
  'WeakRef is not defined',
  'LIDNotifyId', // 外部注入脚本（原生桥/浏览器扩展）引用的未定义全局，堆栈为 ":1:1"，非本站代码
  /AbortError/i,
];

/** 静态资源性能插件：按 URL 忽略（接口、SDK 自身、业务 CDN 等） */
const RESOURCE_IGNORE_URLS: (string | RegExp)[] = [
  /apmplus\.volces\.com/i,
  /zijieapi\.com/i,
  '8848top.com', // belink*.8848top.com、cdn.8848top.com
  'wejourney.top', // BFF：get-current-time、login、order-list 等
  'volccdn.com', // 直播 SDK：mediaservice-fe.volccdn.com
  'byteoc.com', // live.byteoc.com
  'byted-static.com', // unpkg.byted-static.com
  'ibytedapm.com', // lf3-short.ibytedapm.com 监控脚本
  'myqcloud.com', // COS 静态资源、logo 等
];

/**
 * 初始化 WebPro SDK（init + start），仅需调用一次。
 * aid / token 使用项目固定配置，可通过 options 覆盖 pid、userId、plugins 等。
 */
export function initApmplusMonitor(options?: ApmplusInitOptions): void {
  if (initialized || typeof window === 'undefined') return;

  const initConfig: BrowserInitConfig = {
    aid: APM_AID,
    token: APM_TOKEN,
    env: import.meta.env.VITE_NODE_ENV,
    pid: window.location.pathname || '/',
    ...options,
    release: options?.release ?? resolveApmRelease(),
    plugins: {
      ...options?.plugins,
      jsError: {
        ...options?.plugins?.jsError,
        ignoreErrors: [...JS_ERROR_IGNORE_PATTERNS, ...(options?.plugins?.jsError?.ignoreErrors ?? [])],
      },
      resource: {
        ...options?.plugins?.resource,
        ignoreTypes: [...RESOURCE_IGNORE_TYPES],
        ignoreUrls: RESOURCE_IGNORE_URLS,
      },
    },
  };

  browserClient('init', initConfig);
  browserClient('start');
  initialized = true;
}

/** 更新用户/页面等公共配置，需在 init 之后调用 */
export function updateApmplusUserConfig(config: Partial<BrowserUserConfig>): void {
  if (!initialized) return;
  browserClient('config', config);
}

/** 上报自定义日志，对应控制台「自定义监控 - 日志」 */
export function reportApmCustomLog(params: ApmCustomLogParams): void {
  if (!initialized) {
    console.warn('[apmplusMonitor] SDK 未初始化，跳过 sendLog');
    return;
  }
  browserClient('sendLog', params);
}

/** 各级别权重，用于「最小上报级别」过滤 */
const LOG_LEVEL_WEIGHT: Record<ApmLogLevel, number> = {
  [ApmLogLevel.debug]: 10,
  [ApmLogLevel.info]: 20,
  [ApmLogLevel.warn]: 30,
  [ApmLogLevel.error]: 40,
};

/** 线上临时开启 debug 上报的开关（localStorage，排查用） */
const LOG_DEBUG_SWITCH_KEY = 'BELINK-APM-LOG-DEBUG';

/** 是否强制开启 debug 级别上报（便于线上排查，无需发版） */
function isApmDebugEnabled(): boolean {
  if (import.meta.env.VITE_APM_LOG_DEBUG === 'true') return true;
  try {
    return localStorage.getItem(LOG_DEBUG_SWITCH_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * 当前最小上报级别对应权重：
 * - 开启 debug 开关 → debug（全部上报）
 * - 生产环境 → info（丢弃 debug 噪音，压缩日志条数）
 * - 其余环境 → debug
 */
function getMinReportWeight(): number {
  if (isApmDebugEnabled()) return LOG_LEVEL_WEIGHT[ApmLogLevel.debug];
  if (import.meta.env.VITE_NODE_ENV === 'production') return LOG_LEVEL_WEIGHT[ApmLogLevel.info];
  return LOG_LEVEL_WEIGHT[ApmLogLevel.debug];
}

function reportApmLogByLevel(level: ApmLogLevel, content: string, extra?: ApmLogExtraInput): void {
  // 生产默认丢弃 debug 噪音；临时排查可置 localStorage['BELINK-APM-LOG-DEBUG']='1' 或构建期 VITE_APM_LOG_DEBUG=true
  if (LOG_LEVEL_WEIGHT[level] < getMinReportWeight()) return;
  reportApmCustomLog({ content, level, extra: normalizeExtra(extra) });
}

/**
 * WebPro 自定义日志上报（用法同原 clsLogger.info / error）
 *
 * @example
 * apmLogger.info('用户进入直播间', { activityId });
 * apmLogger.error('拉流失败', { code: 500 });
 */
const apmLogger = {
  debug(content: string, extra?: ApmLogExtraInput): void {
    reportApmLogByLevel(ApmLogLevel.debug, content, extra);
  },
  info(content: string, extra?: ApmLogExtraInput): void {
    reportApmLogByLevel(ApmLogLevel.info, content, extra);
  },
  warn(content: string, extra?: ApmLogExtraInput): void {
    reportApmLogByLevel(ApmLogLevel.warn, content, extra);
  },
  error(content: string, extra?: ApmLogExtraInput): void {
    reportApmLogByLevel(ApmLogLevel.error, content, extra);
  },
};

export default apmLogger;

export function isApmplusMonitorInitialized(): boolean {
  return initialized;
}
